import React, { useState, useRef, useEffect, useMemo } from 'react';
import { GenerateTopBar } from './generate-sections/GenerateTopBar';
import { GenerateLeftSidebar } from './generate-sections/GenerateLeftSidebar';
import { GenerateMainContent } from './generate-sections/GenerateMainContent';
import { SimilarProducts } from './generate-sections/SimilarProducts';
import { GenerateProps } from './generate-sections/types';
import { FullscreenModal } from './generate-sub/fullscreen-modal';
import { AdvancedSettingsModal } from './generate-sub/advanced-settings-modal';
import LoraModelSelector from './lora-model-selector';
import { InsufficientCreditsModal } from '../billing/insufficient-credits-modal';
import { useGeneratedImages } from '@/data/ai-image';
import { useProducts } from '@/data/product';
import { useMe } from '@/data/user';
import { useModalAction } from '../modal-views/context';
import { getStoredSettings, saveSettings } from '@/utils/settings-manager';
import { useImageGeneration } from '@/hooks/use-image-generation';
import { aspectRatios } from '@/components/ui/image-size';
import { SortOrder } from '@/types';
import toast from 'react-hot-toast';
import { LoraModel } from '@/types/lora';
import { useLocalStorage } from 'react-use';
import { trackEvent } from '@/data/utils/utils';

const Generate: React.FC<GenerateProps> = ({ product }) => {
  const storedSettings = getStoredSettings(product.id);
  const [isAdvancedModalOpen, setIsAdvancedModalOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const defaultImageSize = useMemo(
    () =>
      product?.default_image_size
        ? aspectRatios.find(
            (ratio) => ratio.name === product.default_image_size
          )
        : aspectRatios[4],
    [product.default_image_size]
  );
  const [currentRatio, setCurrentRatio] = useState(
    storedSettings?.aspectRatio ?? defaultImageSize
  );
  const sliderRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { id, name, trigger_word } = product;
  const { me } = useMe();
  const {
    generatedImages,
    isLoading,
    refetch: refetchGeneratedImages,
    fetchNextPage,
    hasNextPage: hasNextPageGeneratedImages,
    isFetchingNextPage,
    totalCount,
  } = useGeneratedImages({
    product_id: id,
    language: 'en',
    enabled: !!me,
  });
  const {
    generatedImages: publicGeneratedImages,
    isLoading: isLoadingPublic,
    fetchNextPage: fetchNextPagePublic,
    hasNextPage: hasNextPagePublic,
    isFetchingNextPage: isFetchingNextPagePublic,
    totalCount: totalCountPublic,
  } = useGeneratedImages({
    product_id: id,
    language: 'en',
    is_public: true,
  });

  const limitSimilarProducts = 6;

  const {
    products: similarProducts,
    loadMore,
    hasNextPage,
    isLoadingMore,
    isLoading: isLoadingSimilarProducts,
  } = useProducts(
    {
      sortedBy: SortOrder.Desc,
      orderBy: 'orders_count',
      name,
      limit: limitSimilarProducts,
      is_custom_ai_model: true,
      exclude: parseInt(id, 10),
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  const { generateImage, isGenerating, status } = useImageGeneration(
    product,
    refetchGeneratedImages
  );

  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [isLoraModalOpen, setIsLoraModalOpen] = useState(false);
  const [selectedLoraModel, setSelectedLoraModel] = useState<LoraModel | null>(
    storedSettings?.loraModel ?? null
  );
  const [missingTriggerWords, setMissingTriggerWords] = useState<string[]>([]);
  const [requiredTriggers, setRequiredTriggers] = useState<string[]>([
    trigger_word,
  ]);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [stealthModeLocal, setStealthMode] = useLocalStorage(
    'stealth-mode',
    product.status === 'draft'
  );

  const stealthMode = product.status === 'draft' || stealthModeLocal;

  // Create wrapped setters that save settings
  const updateRatio = (newRatio: (typeof aspectRatios)[0]) => {
    setCurrentRatio(newRatio);
    saveSettings(product.id, {
      aspectRatio: newRatio,
      loraModel: selectedLoraModel,
    });
  };

  const updateLoraModel = (newModel: LoraModel | null) => {
    setSelectedLoraModel(newModel);
    saveSettings(product.id, {
      aspectRatio: currentRatio,
      loraModel: newModel,
    });
  };

  // Update reset handler
  const handleResetSettings = () => {
    setCurrentRatio(aspectRatios[4]);
    setSelectedLoraModel(null);
    localStorage.removeItem(`product-${product.id}-settings`);
  };

  const validatePrompt = (promptText: string) => {
    const missing = requiredTriggers.filter(
      (trigger) => !promptText.toLowerCase().includes(trigger.toLowerCase())
    );
    setMissingTriggerWords(missing);
    return missing.length === 0;
  };
  useEffect(() => {
    validatePrompt(prompt);
  }, [prompt, requiredTriggers]);

  const { openModal } = useModalAction();
  const handleGenerate = () => {
    if (!me) {
      openModal('REGISTER');
      trackEvent('generate_image_unauthenticated', { product_id: id });
      return;
    }

    const availableCredits = me?.wallet?.available_points || 0;
    if (availableCredits < 1) {
      setShowCreditsModal(true);
      trackEvent('insufficient_credits_generate', {
        credits: availableCredits,
        product_id: id,
      });
      return;
    }

    if (!validatePrompt(prompt)) {
      toast.error('Please include all required trigger words');
      trackEvent('invalid_prompt_generate', {
        prompt,
        missing_triggers: missingTriggerWords,
        product_id: id,
      });
      return;
    }

    trackEvent('start_image_generation', {
      prompt,
      product_id: id,
      image_size: currentRatio?.name,
      lora_model: selectedLoraModel?.id,
      is_public: !stealthMode,
    });

    generateImage({
      prompt,
      product_id: id,
      image_size: currentRatio?.name ?? '1:1',
      additional_lora_product_ids: selectedLoraModel?.id
        ? [selectedLoraModel.id]
        : [],
      is_public: !stealthMode,
    });
  };

  // Update required triggers when selectedLoraModel changes
  useEffect(() => {
    const newTriggers = selectedLoraModel?.trigger_word
      ? [trigger_word, selectedLoraModel.trigger_word]
      : [trigger_word];
    setRequiredTriggers(newTriggers);
  }, [selectedLoraModel, trigger_word]);

  return (
    <div className="h-full min-h-screen p-2 sm:p-4 md:px-6 lg:px-8 lg:pt-6">
      <div className="mx-auto max-w-screen-xl">
        <GenerateTopBar product={product} />

        <div className="flex flex-col gap-4 xl:flex-row xl:gap-8">
          <GenerateLeftSidebar
            product={product}
            sliderRef={sliderRef}
            setPrompt={setPrompt}
            setSelectedLoraModel={setSelectedLoraModel}
            textareaRef={textareaRef}
          />

          <GenerateMainContent
            prompt={prompt}
            setPrompt={setPrompt}
            handleGenerate={handleGenerate}
            isGenerating={isGenerating}
            currentRatio={currentRatio}
            selectedLoraModel={selectedLoraModel}
            setSelectedLoraModel={setSelectedLoraModel}
            missingTriggerWords={missingTriggerWords}
            setIsAdvancedModalOpen={setIsAdvancedModalOpen}
            handleResetSettings={handleResetSettings}
            validatePrompt={validatePrompt}
            requiredTriggers={requiredTriggers}
            stealthMode={stealthMode}
            setStealthMode={setStealthMode}
            generatedImages={generatedImages}
            publicGeneratedImages={publicGeneratedImages}
            imagesProps={{
              isLoading,
              hasNextPage: hasNextPageGeneratedImages ?? false,
              isFetchingNextPage,
              fetchNextPage,
              totalCount,
            }}
            publicImagesProps={{
              isLoading: isLoadingPublic,
              hasNextPage: hasNextPagePublic ?? false,
              isFetchingNextPage: isFetchingNextPagePublic,
              fetchNextPage: fetchNextPagePublic,
              totalCount: totalCountPublic,
            }}
            setFullscreenImage={setFullscreenImage}
            status={status}
            textareaRef={textareaRef}
            product={product}
          />
        </div>

        <SimilarProducts
          product={product}
          similarProducts={similarProducts}
          loadMore={loadMore}
          hasNextPage={hasNextPage}
          isLoadingMore={isLoadingMore}
          isLoadingSimilarProducts={isLoadingSimilarProducts}
          limitSimilarProducts={limitSimilarProducts}
        />
      </div>

      {/* Modals */}
      {fullscreenImage && (
        <FullscreenModal
          imageUrl={fullscreenImage}
          prompt={
            generatedImages?.find((image) =>
              image.image_urls?.some((url) => url.original === fullscreenImage)
            )?.prompt || ''
          }
          onClose={() => setFullscreenImage(null)}
        />
      )}
      <LoraModelSelector
        isOpen={isLoraModalOpen}
        onClose={() => setIsLoraModalOpen(false)}
        onSelect={setSelectedLoraModel}
      />
      <AdvancedSettingsModal
        isOpen={isAdvancedModalOpen}
        onClose={() => setIsAdvancedModalOpen(false)}
        currentRatio={currentRatio}
        setCurrentRatio={updateRatio}
        selectedLoraModel={selectedLoraModel}
        setSelectedLoraModel={updateLoraModel}
        setIsLoraModalOpen={setIsLoraModalOpen}
        stealthMode={stealthMode}
        setStealthMode={setStealthMode}
      />
      <InsufficientCreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
      />
    </div>
  );
};

export default Generate;
